import React from "react";
import clone from "lodash/clone";
import sortBy from "lodash/sortBy";
import values from "lodash/values";
import moment from "moment";
import Fuse from "fuse.js";
import { CircularProgress } from "material-ui";
import List from "../list/list.jsx";
import EventFilterEditor from "./event-filter-editor.jsx";
import EventListItem from "./event-list-item.jsx";

import * as util from "../../../util";

import TagUtil from "../../../utils/TagUtil.js";
import EventGridListItem from "./event-grid-list-item.jsx";
import EventTimeline from "./event-timeline.jsx";
import EventGrid from "./event-grid.jsx";

import TicketStore from "../../../stores/TicketStore";
import WishlistStore from "../../../stores/WishlistStore";

class EventList extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      showFilterEditor: false,
      filteredEvents: [],
      tags: [],
      venues: [],
      eventViewFilter: "physical"
    };

    this._onTagChange = this._onTagChange.bind(this);
    this._onViewModeChange = this._onViewModeChange.bind(this);
    this._onEventViewFilterChange = this._onEventViewFilterChange.bind(this);
    this._onFilterOpen = this._onFilterOpen.bind(this);
    this._onFilterClose = this._onFilterClose.bind(this);
    this._onSearchChange = this._onSearchChange.bind(this);
    this._onFilterVenueChange = this._onFilterVenueChange.bind(this);
    this._onJustMyEventsChange = this._onJustMyEventsChange.bind(this);
  }

  shouldComponentUpdate() {
    return util.shouldComponentUpdateDeep.apply(this, arguments);
  }

  _onTagChange(tag, enable, stateOptions) {
    let tags = clone(this.props.filterTags);
    if (enable) {
      tags[tag] = true;
    } else {
      delete tags[tag];
    }
    this.props.onStateChange({ filterTags: tags }, stateOptions);
  }

  _onSearchChange(e) {
    this.props.onStateChange({ search: e.target.value });
  }

  _onViewModeChange(mode) {
    this.props.onStateChange({ viewMode: mode });
  }

  _onEventViewFilterChange(filter) {
    this.setState({ eventViewFilter: filter }, function () {
      this.componentWillReceiveProps(this.props);
    });
  }

  _onFilterVenueChange(venue, enable, stateOptions) {
    let venues = clone(this.props.filterVenues);
    if (enable) {
      venues[venue] = true;
    } else {
      delete venues[venue];
    }
    this.props.onStateChange({ filterVenues: venues }, stateOptions);
  }

  _onJustMyEventsChange(enabled) {
    this.props.onStateChange({ justMyEvents: enabled });
  }

  _onFilterOpen() {
    this.setState({ showFilterEditor: true });
  }

  _onFilterClose() {
    this.setState({ showFilterEditor: false }, () => {
      this.props.onStateChange({
        filterTags: {},
        search: "",
        filterVenues: {}
      });
    });
  }

  static get contextTypes() {
    return {
      isLarge: React.PropTypes.bool,
      // Ensure that Configurator changes trigger a re-render
      tenant: React.PropTypes.object
    };
  }

  componentWillMount() {
    const eventViewFilter = this.props.appSettings.schedule_include_virtual
      ? "hybrid"
      : "physical";
    this.setState({ eventViewFilter: eventViewFilter }, function () {
      this.componentWillReceiveProps(this.props);
    });
  }

  componentWillReceiveProps(newProps) {
    // Events (strip unnamed/untimed ones)
    let { events } = newProps;
    events = Object.keys(events)
      .filter(e => {
        // Remove previous search match data
        delete events[e]._search_match_details;
        return (
          events[e].name !== "Unnamed Event" &&
          events[e].name !== "Unnamed Virtual Screening" &&
          (events[e].start_time || !events[e].is_dated) &&
          events[e].visibility !== "hidden"
        );
      })
      .reduce(function (obj, e) {
        if (
          newProps.appSettings.schedule_include_virtual !== true &&
          events[e].is_virtual
        ) {
          return obj;
        }
        obj[e] = events[e];
        return obj;
      }, {});

    if (this.context.tenant.virtual_protected_tag_list && events) {
      let passList = Object.values(this.props.passes || {});
      events = TagUtil.filterEventTagsByPass(
        events,
        this.context.tenant.virtual_protected_tag_list,
        passList
      );
    }

    // Process Events View Filter
    const eventViewFilter = this.state.eventViewFilter;
    events = Object.keys(events).reduce(function (obj, e) {
      switch (eventViewFilter) {
        case "physical":
          if (!events[e].is_virtual) {
            obj[e] = events[e];
            return obj;
          }
          break;

        case "virtual":
          if (events[e].is_virtual) {
            obj[e] = events[e];
            return obj;
          }
          break;

        default:
          obj[e] = events[e];
          return obj;
      }
      return obj;
    }, {});

    // Filtered events
    let isFiltered =
        newProps.filterName ||
        Object.keys(newProps.filterVenues).length ||
        Object.keys(newProps.filterTags).length,
      filteredEvents =
        isFiltered || newProps.justMyEvents
          ? Object.keys(events)
              .filter(e => {
                if (
                  newProps.filterName &&
                  events[e].name
                    .toLowerCase()
                    .indexOf(newProps.filterName.toLowerCase()) === -1
                ) {
                  return false;
                }

                if (
                  Object.keys(newProps.filterVenues).length &&
                  !newProps.filterVenues[events[e].venue.id]
                ) {
                  return false;
                }

                if (
                  newProps.justMyEvents &&
                  !(
                    WishlistStore.isWishlisted({ type: "events", id: e }) ||
                    (events[e].person && events[e].person.ticket_count > 0) ||
                    TicketStore.getTicketsByEventId(e).length
                  )
                ) {
                  return false;
                }

                if (Object.keys(newProps.filterTags).length && events[e].tags) {
                  return events[e].tags.some(t => newProps.filterTags[t.id]);
                }

                // Don't show TBAs when filtering
                if (events[e].visibility === "tba") {
                  return false;
                }

                return true;
              })
              .reduce(function (obj, e) {
                obj[e] = events[e];
                return obj;
              }, {})
          : events;

    // Tags & venues
    let tags = {},
      venues = {};
    Object.keys(events).forEach(function (k) {
      events[k].tags.forEach(t => {
        tags[t.id] = {
          id: t.id,
          name: t.name,
          color: t.color,
          visible: t.visible
        };
      });

      if (events[k].is_virtual !== true) {
        let venue = events[k].venue;
        if (venue && venue.id && !venues[venue.id]) {
          venues[venue.id] = {
            id: venue.id,
            name: venue.short_name || venue.name,
            color: venue.color
          };
        }
      } else {
        const virtualEvent = events[k];

        // Determine if event is Preorderable
        const isInAvailabilityWindow =
          !virtualEvent.start_time ||
          (Date.now() >= new Date(virtualEvent.start_time) &&
            Date.now() < new Date(virtualEvent.end_time));
        const isInFuture =
          virtualEvent.start_time &&
          !isInAvailabilityWindow &&
          Date.now() < new Date(virtualEvent.start_time);
        const isPreorderable = virtualEvent.allow_preorder && isInFuture;

        //NOTES:  Pre-order now, Unlock now, or Watch now - check watch site logic
        // check to see if film content exists (not livestream) or if
        // if is a livestream, if it has already happened
        const now = moment();
        let hasContent = false;
        for (let z = 0; z < virtualEvent.films.length; z++) {
          const thisFilm = virtualEvent.films[z];
          if (thisFilm.type !== "livestream") {
            hasContent = true;
            break;
          } else if (now.isSameOrAfter(thisFilm.end_time)) {
            hasContent = true;
          }
        }
        // handle livestreams that have not occurred yet but are preorderable
        if (virtualEvent.isPreorderable) {
          hasContent = true;
        }
        // exclude from virtual lists if livestream and has not happened yet
        if (!hasContent) {
          virtualEvent.exclude_from_virtual = true;
        }
      }
    });

    // Now search filtered events
    if (newProps.search) {
      let fuse = new Fuse(Object.values(filteredEvents), {
        keys: [
          { name: "name", weight: 0.7 },
          { name: "short_description", weight: 0.3 }
        ],
        id: "id",
        includeMatches: true,
        threshold: 0.1,
        location: 0,
        distance: 100000,
        maxPatternLength: 32,
        minMatchCharLength: 3
      });
      filteredEvents = fuse
        .search(newProps.search.trim())
        .map(result => {
          filteredEvents[result.item]._search_match_details = result.matches;
          return result.item;
        })
        .reduce(function (obj, e) {
          obj[e] = filteredEvents[e];
          return obj;
        }, {});
      isFiltered = true;
    }

    this.setState({
      showFilterEditor: this.state.showFilterEditor || isFiltered,
      filteredEvents: filteredEvents,
      tags: sortBy(values(tags), "name"),
      venues: sortBy(values(venues), "name")
    });
  }

  render() {
    let { isLarge } = this.context,
      { showLoadingIndicator, events } = this.props,
      { showFilterEditor, filteredEvents, tags, venues } = this.state;

    return (
      <div>
        <EventFilterEditor
          events={events}
          tags={tags}
          venues={venues}
          filterTags={this.props.filterTags}
          onTagChange={this._onTagChange}
          viewMode={this.props.viewMode}
          includeEventViewFilter={
            this.props.appSettings.schedule_include_virtual
          }
          eventViewFilter={this.state.eventViewFilter}
          search={this.props.search}
          filterVenues={this.props.filterVenues}
          justMyEvents={this.props.justMyEvents}
          onViewModeChange={this._onViewModeChange}
          onEventViewFilterChange={this._onEventViewFilterChange}
          onSearchChange={this._onSearchChange}
          onFilterVenueChange={this._onFilterVenueChange}
          onJustMyEventsChange={this._onJustMyEventsChange}
          onClose={this._onFilterClose}
          onOpen={this._onFilterOpen}
          open={showFilterEditor}
          hasWishlist={this.props.hasWishlist}
          isLarge={isLarge}
        />
        {!showLoadingIndicator ? (
          this.props.viewMode === "timeline" ? (
            <EventTimeline
              events={filteredEvents}
              range={this.props.timelineRange}
              venues={venues}
              fetchPast={this.props.fetchPast}
              onStateChange={this.props.onStateChange}
            />
          ) : this.props.viewMode === "grid" ? (
            <EventGrid
              appSettings={this.props.appSettings}
              events={filteredEvents}
              date={this.props.gridDate}
              venues={venues}
              fetchPast={this.props.fetchPast}
              onStateChange={this.props.onStateChange}
              ListItem={EventListItem}
              listItemProps={{
                isLarge: false,
                onTagChange: this._onTagChange,
                onFilterVenueChange: this._onFilterVenueChange,
                appSettings: this.props.appSettings
              }}
              groupItemsBy={this.props.groupItemsBy}
            />
          ) : (
            <List
              useVirtualizedMode={this.context.tenant.virtualize_lists}
              items={filteredEvents}
              viewMode={
                this.props.viewMode === "cover" ? "grid" : this.props.viewMode
              } // backwards compat
              eventViewFilter={this.state.eventViewFilter}
              timeGetter={e => e.start_time}
              endTimeGetter={e => e.end_time}
              secondarySortGetter={e =>
                `${e.venue ? e.venue.name : ""}${e.name}`
              }
              offerFetchPast={this.props.offerFetchPast}
              fetchPast={this.props.fetchPast}
              fetchingPast={this.props.fetchingPast}
              groupItemsBy={this.props.groupItemsBy}
              GridListItem={EventGridListItem}
              ListItem={EventListItem}
              listItemProps={{
                isLarge: isLarge,
                onTagChange: this._onTagChange,
                onFilterVenueChange: this._onFilterVenueChange,
                appSettings: this.props.appSettings,
                useVirtualizedMode: this.context.tenant.virtualize_lists
              }}
            />
          )
        ) : (
          <div style={{ padding: 10, textAlign: "center" }}>
            <CircularProgress
              mode="indeterminate"
              size={119}
              thickness={7}
              style={{ margin: 10.5 }}
            />
          </div>
        )}
      </div>
    );
  }
}

export default EventList;



// WEBPACK FOOTER //
// ./src/app/components/partials/event-list/event-list.jsx